// Route to page. `route` is the one signal that decides what is on screen; this switch is the
// one place that turns it into a page. Add a route by adding a case here and a match in
// `router.ts`, nothing else.
//
// Pages that need a session render inside the full shell; the welcome and sign-in forms render in the
// signed-out frame the shell falls back to without one. An output window and the guest join window are
// the exception to both: each is a full-window surface with no session chrome, so both render on their
// own, with no shell around them at all.
//
// Until `boot()` has answered, the session is `undefined` and every route shows the loading line, so a
// deep link never flashes a page its viewer may not be allowed to see before the sign-in redirect.
//
// A session that may only open Stage and Singer sees its screens in place of every other in-shell page
// (`guardedRoute`); the server's refusals stay the real guard.

import { useEffect } from 'preact/hooks';

import { can, outputOnly, serverUpdating, session } from './app-state.js';
import { AnotherAccountNotice } from './components/another-account-notice.js';
import { AppShell } from './components/app-shell.js';
import { LoadingLine } from './components/loading-line.js';
import { ServerUpdating } from './components/server-updating.js';
import { OutputUpdate } from './components/output-update.js';
import { lazy } from './lazy.js';
import { NoAccessPage } from './pages/no-access.js';
import { NotFoundPage } from './pages/not-found.js';
import { SignInPage } from './pages/sign-in.js';
import { WelcomePage } from './pages/welcome.js';
import { homeOf, startsOnHome } from './request.js';
import { navigate, route, type Route } from './router.js';
import { requestSignOut } from './sign-out.js';

import type { JSX } from 'preact';

const InvitePage = lazy(() => import('./pages/invite.js').then((module) => module.InvitePage));
const PasswordChangePage = lazy(() => import('./pages/password-change.js').then((module) => module.PasswordChangePage));
const OutputPage = lazy(() => import('./pages/output.js').then((module) => module.OutputPage));
const JoinPage = lazy(() => import('./pages/join.js').then((module) => module.JoinPage));
const WatchPage = lazy(() => import('./pages/watch.js').then((module) => module.WatchPage));
const HomePage = lazy(() => import('./workspace/Home.js').then((module) => module.Home));
const ServicesPage = lazy(() => import('./workspace/Dashboard.js').then((module) => module.Dashboard));
const ServicePage = lazy(() => import('./pages/service.js').then((module) => module.ServicePage));
const AdminOverviewPage = lazy(() => import('./pages/admin-overview.js').then((module) => module.AdminOverviewPage));
const AdminUsersPage = lazy(() => import('./pages/admin-users.js').then((module) => module.AdminUsersPage));
const AdminSettingsPage = lazy(() => import('./pages/admin-settings.js').then((module) => module.AdminSettingsPage));
const AdminChurchPage = lazy(() => import('./pages/admin-church.js').then((module) => module.AdminChurchPage));
const AdminSongTransferCredentialsPage = lazy(() =>
  import('./pages/admin-song-transfer-credentials.js').then((module) => module.AdminSongTransferCredentialsPage));
const FirstRunPage = lazy(() => import('./setup/first-run.js').then((module) => module.FirstRunPage));
const AdminAuditPage = lazy(() => import('./pages/admin-audit.js').then((module) => module.AdminAuditPage));
const AdminJobsPage = lazy(() => import('./pages/admin-jobs.js').then((module) => module.AdminJobsPage));
const AdminOperationsPage = lazy(() => import('./pages/admin-operations.js').then((module) => module.AdminOperationsPage));
const AdminBackupsPage = lazy(() => import('./pages/admin-backups.js').then((module) => module.AdminBackupsPage));
const AdminWhatsNewPage = lazy(() => import('./pages/admin-whats-new.js').then((module) => module.AdminWhatsNewPage));
const AdminStoragePage = lazy(() => import('./pages/admin-storage.js').then((module) => module.AdminStoragePage));
const AdminSongCatalogPage = lazy(() => import('./pages/admin-song-catalog.js').then((module) => module.AdminSongCatalogPage));
const AdminMediaCleanupPage = lazy(() =>
  import('./pages/admin-media-cleanup.js').then((module) => module.AdminMediaCleanupPage));
const AdminIntegrationsPage = lazy(() => import('./pages/admin-integrations.js').then((module) => module.AdminIntegrationsPage));
const AdminBiblesPage = lazy(() => import('./pages/admin-bibles.js').then((module) => module.AdminBiblesPage));
const AdminLanguagesPage = lazy(() => import('./pages/admin-languages.js').then((module) => module.AdminLanguagesPage));
const AdminSlideLabelsPage = lazy(() => import('./pages/admin-slide-labels.js').then((module) => module.AdminSlideLabelsPage));
const LayoutList = lazy(() => import('./admin/layouts/LayoutList.js').then((module) => module.LayoutList));
const LayoutEditor = lazy(() => import('./admin/layouts/LayoutEditor.js').then((module) => module.LayoutEditor));
const LayoutVariantsPage = lazy(() => import('./admin/layouts/LayoutVariants.js').then((module) => module.LayoutVariantsPage));
const TemplateList = lazy(() => import('./admin/templates/TemplateList.js').then((module) => module.TemplateList));
const TemplateEditor = lazy(() => import('./admin/templates/TemplateEditor.js').then((module) => module.TemplateEditor));
const HistoryPage = lazy(() => import('./pages/history.js').then((module) => module.HistoryPage));
const SecurityPage = lazy(() => import('./pages/account-security.js').then((module) => module.SecurityPage));
const AccountNotificationsPage = lazy(() =>
  import('./pages/account-notifications.js').then((module) => module.AccountNotificationsPage));
const DisplaySettingsPage = lazy(() => import('./pages/account-display.js').then((module) => module.DisplaySettingsPage));
const OfflineCheckPage = lazy(() => import('./pages/offline-check.js').then((module) => module.OfflineCheckPage));
const ScreensPage = lazy(() => import('./pages/screens.js').then((module) => module.ScreensPage));
const WorkspacePage = lazy(() => import('./workspace/Workspace.js').then((module) => module.Workspace)) as
  (props: { readonly id: string }) => JSX.Element;
const NewServicePage = lazy(() => import('./workspace/NewService.js').then((module) => module.NewService));
const ReadinessPage = lazy(() => import('./readiness/ReadinessPage.js').then((module) => module.ReadinessPage));
const RehearsePage = lazy(() => import('./pages/rehearse.js').then((module) => module.RehearsePage));
const ContentLibraryPage = lazy(() => import('./library/ContentLibrary.js').then((module) => module.ContentLibrary));
const MediaLibraryPage = lazy(() => import('./library/MediaLibrary.js').then((module) => module.MediaLibrary));

/**
 * The routes a session that may only open Stage and Singer still sees: its screens, its account pages, a missing
 * page, the routes boot moves on from (Home, sign-in, welcome; boot picks where they lead, and «Add account» lives
 * on sign-in), the setup page, which shows a missing page to anyone who may not change settings, and the
 * full-window routes `App` renders without the shell.
 */
export const OUTPUT_ONLY_ROUTES: ReadonlySet<Route['name']> = new Set<Route['name']>([
  'screens',
  'account-security',
  'account-notifications',
  'account-display',
  'offline-check',
  'not-found',
  'sign-in',
  'home',
  'welcome',
  'invite',
  'setup',
  'output',
  'join',
  'watch',
  'watch-service',
]);

/** The route the shell shows: `current`, or the screens page where an output-only session asks for anything else. */
export function guardedRoute(current: Route): Route {
  if (!outputOnly() || OUTPUT_ONLY_ROUTES.has(current.name)) {
    return current;
  }

  return { name: 'screens' };
}

/** What the current route renders inside the shell. */
function Page(): JSX.Element {
  const asked = route.value;
  const current = guardedRoute(asked);
  const guarded = current !== asked;

  // The address follows the page shown, replacing the refused one so Back does not return to it. From an effect,
  // never during render, where a navigate would change the route signal mid-render.
  useEffect(() => {
    if (guarded) {
      navigate('/screens', { replace: true });
    }
  }, [guarded, asked]);

  // A signed-in session that starts elsewhere (its unfinished setup, its screens) reaching Home in the app goes on
  // to its start, so it never waits on a loading line.
  const elsewhere = current.name === 'home' && session.value != null && !startsOnHome();
  useEffect(() => {
    if (elsewhere) {
      navigate(homeOf(), { replace: true });
    }
  }, [elsewhere]);

  if (serverUpdating.value !== undefined) {
    return <ServerUpdating />;
  }

  if (session.value === undefined) {
    return <LoadingLine />;
  }

  if (session.value?.account?.mustChangePassword === true) {
    return <PasswordChangePage />;
  }

  switch (current.name) {
    case 'services':
      // The Live choice is an Operator's, who controls a service without listing any; the list itself is for managers.
      return can('services.manage') || (current.live !== undefined && can('presentation.control'))
        ? <ServicesPage {...(current.live === undefined ? {} : { live: current.live })} />
        : <NoAccessPage />;
    case 'service-new':
      return <NewServicePage {...(current.copy === undefined ? {} : { copy: current.copy })} />;
    case 'service':
      return can('services.manage') ? <WorkspacePage id={current.id} /> : <NoAccessPage />;
    case 'service-live':
      return <ServicePage id={current.id} />;
    case 'service-readiness':
      return <ReadinessPage id={current.id} intent={current.intent} />;
    case 'service-rehearse':
      return <RehearsePage id={current.id} />;
    case 'layout-variants':
      return <LayoutVariantsPage />;
    case 'library':
      return can('content.edit') ? <ContentLibraryPage /> : <NoAccessPage />;
    case 'media':
      return <MediaLibraryPage />;
    case 'admin-overview':
      return <AdminOverviewPage />;
    case 'admin-users':
      return <AdminUsersPage />;
    case 'admin-settings':
      return <AdminSettingsPage />;
    case 'admin-church':
      return <AdminChurchPage />;
    case 'admin-song-transfer-credentials':
      return <AdminSongTransferCredentialsPage />;
    case 'admin-audit':
      return <AdminAuditPage />;
    case 'admin-jobs':
      return <AdminJobsPage job={current.job} />;
    case 'admin-operations':
      return <AdminOperationsPage />;
    case 'admin-backups':
      return <AdminBackupsPage />;
    case 'admin-whats-new':
      return <AdminWhatsNewPage />;
    case 'admin-storage':
      return <AdminStoragePage />;
    case 'admin-song-catalog':
      return <AdminSongCatalogPage />;
    case 'admin-media-cleanup':
      return <AdminMediaCleanupPage />;
    case 'admin-integrations':
      return <AdminIntegrationsPage />;
    case 'admin-bibles':
      return <AdminBiblesPage />;
    case 'admin-languages':
      return <AdminLanguagesPage />;
    case 'admin-slide-labels':
      return <AdminSlideLabelsPage />;
    case 'admin-layouts':
      return <LayoutList />;
    case 'admin-layout':
      return <LayoutEditor key={current.id} id={current.id} />;
    case 'admin-templates':
      return <TemplateList />;
    case 'admin-template':
      return <TemplateEditor key={current.id} id={current.id} />;
    case 'account-security':
      return <SecurityPage />;
    case 'account-notifications':
      return <AccountNotificationsPage />;
    case 'account-display':
      return <DisplaySettingsPage />;
    case 'offline-check':
      return <OfflineCheckPage />;
    case 'screens':
      return <ScreensPage />;
    case 'content-history':
      return <HistoryPage contentId={current.contentId} template={current.template === true} />;
    case 'setup':
      return <FirstRunPage />;
    case 'welcome':
      return <WelcomePage />;
    case 'invite':
      return <InvitePage />;
    case 'sign-in':
      return <SignInPage next={current.next} notice={current.notice} add={current.add === true} />;
    // `/` is Home. `boot()` moves a signed-out browser on to `/welcome` or `/sign-in`, and the effect above a session
    // that starts elsewhere; until then there is nothing to show.
    case 'home':
      return session.value !== null && startsOnHome() ? <HomePage /> : <LoadingLine />;
    // Rendered without the shell by `App`; listed so this switch stays total over every route.
    case 'output':
      return <OutputPage kind={current.kind} />;
    case 'join':
      return <JoinPage />;
    case 'watch':
    case 'watch-service':
      return <WatchPage />;
    case 'not-found':
      return <NotFoundPage />;
  }
}

export interface AppProps {
  /** Replaces the usual session-ending action where an embedding host needs to own it. */
  readonly onSignOut?: () => void;
}

/** The whole client: an output surface on its own, every other route inside the shell. */
export function App({ onSignOut = () => void requestSignOut() }: AppProps): JSX.Element {
  const current = route.value;
  if (current.name === 'output' && session.value === undefined) {
    return <LoadingLine />;
  }

  if (current.name === 'output') {
    return (
      <>
        <OutputPage kind={current.kind} />
        <OutputUpdate />
        <AnotherAccountNotice />
      </>
    );
  }

  if (current.name === 'join') {
    return (
      <>
        <JoinPage />
        <OutputUpdate />
        <AnotherAccountNotice />
      </>
    );
  }

  if (current.name === 'watch' || current.name === 'watch-service') {
    return (
      <>
        <WatchPage />
        <OutputUpdate />
        <AnotherAccountNotice />
      </>
    );
  }

  return (
    <>
      <AppShell onSignOut={onSignOut}>
        <Page />
      </AppShell>
      <AnotherAccountNotice />
    </>
  );
}
